import { MouseEvent, useState } from 'react';
import { Button, Divider, IconButton, Menu, MenuItem } from '@mui/material';

import { Icon } from '../Icon';

import OptionMenuItem from './components/OptionMenuItem';
import { OptionsMenuProps, OptionType } from './props';

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const OptionsMenu: React.FC<OptionsMenuProps> = (props) => {
  const {
    type = 'icon',
    icon,
    options,
    menuProps,
    iconProps,
    align,
    direction = 'ltr',
    buttonProps,
    iconButtonProps,
    dividerProps,
    children,
  } = props;

  // =============== STATE
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // =============== EVENTS
  const onHandleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onHandleClose = () => {
    setAnchorEl(null);
  };

  // =============== variable
  const defaultAlign = direction === 'ltr' ? 'right' : 'left';
  const behaviorAlign = align ?? defaultAlign;
  const iconToDisplay = icon ?? (
    <Icon icon="mdi:dots-vertical" {...iconProps} />
  );

  // =============== VIEWS
  const renderTriggerView = () => {
    if (type === 'button') {
      return (
        <Button onClick={onHandleClick} {...buttonProps}>
          {children ?? iconToDisplay}
        </Button>
      );
    }
    return (
      <IconButton
        aria-haspopup="true"
        onClick={onHandleClick}
        {...iconButtonProps}
      >
        {iconToDisplay}
      </IconButton>
    );
  };

  return (
    <>
      {renderTriggerView()}
      <Menu
        {...menuProps}
        keepMounted
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onHandleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: behaviorAlign,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: behaviorAlign,
        }}
      >
        {options.map((option: OptionType, index: number) => {
          // if option is divider
          if (option === 'divider') {
            return <Divider key={`divider-${index}`} {...dividerProps} />;
          }

          // if option is string
          if (typeof option === 'string') {
            return (
              <MenuItem key={`item-${index}`} onClick={onHandleClose}>
                {option}
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={`item-${index}`}
              {...option.menuItemProps}
              {...(option.href && { sx: { p: 0 } })}
              onClick={(e) => {
                onHandleClose();
                option?.menuItemProps?.onClick?.(e);
              }}
            >
              <OptionMenuItem option={option}>
                {option.icon ? option.icon : null}
                {option.text}
              </OptionMenuItem>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export * from './props';
export default OptionsMenu;
